import { Fragment, useEffect, useState } from "react";
import "./NewsDetails.css";
import { GetNewsDetails } from "api/GetNewsDetails";
import { extractPathFromURL } from "utils/news/extractPathFromUrl";
import { useLocation } from "react-router-dom";
import { Code } from "@chakra-ui/react";
import { calculateTimeAgo } from "utils/news/calculateTimeAgo";
import { useIntl } from "react-intl";

type NewsDetail = {
  bannerImgSrc: string;
  title_en?: string;
  post_date: string;
  content_en?: string;
  main_img_en: string;
};

const NewsDetails = () => {
  const [newsDetails, setNewsDetails] = useState<NewsDetail>(null);
  const [timeMessage, setTimeMessage] = useState(null);

  const pathname = useLocation().pathname;

  const shortUri = extractPathFromURL(pathname);

  const intl = useIntl();

  useEffect(() => {
    if (!newsDetails) {
      try {
        GetNewsDetails(shortUri).then(({ data }) => {
          setNewsDetails(data.data.article);
        });
      } catch (error) {
        // console.error(error);
      }
    }
  });

  useEffect(() => {
    let timeMessage = "";

    if (newsDetails) {
      const daysAgo = calculateTimeAgo(newsDetails.post_date).daysAgo;
      timeMessage =
        daysAgo < 1
          ? intl.formatMessage({ id: "todayMessage", defaultMessage: "today" })
          : daysAgo >= 30
          ? intl.formatMessage(
              {
                id: "monthsAgoMessage",
                defaultMessage:
                  "{monthsAgo, plural, one {# month} other {# months}} ago",
              },
              { monthsAgo: Math.floor(daysAgo / 30) }
            )
          : daysAgo >= 7
          ? intl.formatMessage(
              {
                id: "weeksAgoMessage",
                defaultMessage:
                  "{weeksAgo, plural, one {# week} other {# weeks}} ago",
              },
              { weeksAgo: Math.floor(daysAgo / 7) }
            )
          : intl.formatMessage(
              {
                id: "daysAgoMessage",
                defaultMessage:
                  "{daysAgo, plural, one {# day} other {# days}} ago",
              },
              { daysAgo }
            );
    }
    setTimeMessage(timeMessage);
  }, [newsDetails, intl]);

  return (
    newsDetails && (
      <div>
        <section
          className="news-banner-area"
          style={{
            backgroundImage: `url(https://www.ipscore.app/${newsDetails.main_img_en})`,
          }}
        >
          <div className="news-info-area">
            <h1>{newsDetails.title_en}</h1>
            <p>{`${intl.formatMessage({
              id: "PublishedText",
            })} ${timeMessage}`}</p>
          </div>
        </section>
        <section className="news-content-area">
          {JSON.parse(newsDetails.content_en).map((item, index) => {
            switch (Object.keys(item)[0]) {
              case "heading":
                return (
                  <h2 className="news-page-heading" key={index}>
                    {item.heading}
                  </h2>
                );
              case "paragraph":
                return (
                  <p className="news-page-paragraph" key={index}>
                    {item.paragraph}
                  </p>
                );
              case "jsonOutput":
                const jsonLines = item.jsonOutput
                  .split("\n")
                  .map((line, index) => <div key={index}>{line}</div>);
                return (
                  <div className="news-page-json" key={index}>
                    {jsonLines}
                  </div>
                );
              case "link":
                return (
                  <Fragment key={index}>
                    {item.link.preLinkText && (
                      <p className="news-page-pre-link-text">
                        {item.link.preLinkText}
                      </p>
                    )}
                    <a
                      className="news-page-link"
                      key={index}
                      href={item.link.path}
                    >
                      {item.link.linkText}
                    </a>
                  </Fragment>
                );
              case "image":
                return (
                  <img
                    className="news-page-img"
                    key={index}
                    src={item.image.imageSrc}
                    alt={item.image.alt}
                  />
                );
              case "code":
                const lines = item.code
                  .split("\n")
                  .map((line, index) => <div key={index}>{line}</div>);
                return (
                  <Code className="news-page-code" key={item.code}>
                    {lines}
                  </Code>
                );
              default:
                return null;
            }
          })}
        </section>
      </div>
    )
  );
};

export default NewsDetails;